import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ModernHeroSection = () => {
  const navigate = useNavigate();
  const [isTamil, setIsTamil] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const translations = {
    title: {
      en: "THE BHARAT SCOUTS AND GUIDES",
      ta: "பாரத சாரணர் மற்றும் சாரணியர்"
    },
    subtitle: {
      en: "Tamil Nadu",
      ta: "தமிழ்நாடு"
    },
    tagline: {
      en: "Empowering Youth Through Service and Leadership",
      ta: "சேவை மற்றும் தலைமைத்துவம் மூலம் இளைஞர்களை மேம்படுத்துதல்"
    },
    description: {
      en: "Join us in shaping the future leaders of tomorrow through adventure, service, and personal development.",
      ta: "சாகச, சேவை மற்றும் தனிப்பட்ட வளர்ச்சி மூலம் நாளைய தலைவர்களை உருவாக்குவதில் எங்களுடன் இணையுங்கள்."
    },
    learnMore: {
      en: "Learn More",
      ta: "மேலும் அறிக"
    },
    officeBearers: {
      en: "Office Bearers",
      ta: "அலுவலக பொறுப்பாளர்கள்"
    }
  };

  const slides = [
    {
      image: '/Images/hero1.png',
      alt: 'Scouts and Guides Activity 1'
    },
    {
      image: '/Images/hero2.png',
      alt: 'Scouts and Guides Activity 2'
    },
    {
      image: '/Images/hero3.png',
      alt: 'Scouts and Guides Activity 3'
    },
       {
      image: '/Images/hero4.png',
      alt: 'Scouts and Guides Activity 3'
    },
  ];

  useEffect(() => {
    setIsVisible(true);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
            />
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative h-full flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Title */}
            <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight transition-all duration-1000 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              {translations.title[isTamil ? 'ta' : 'en']}
            </h1>

            {/* Subtitle */}
            <div className={`text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              {translations.subtitle[isTamil ? 'ta' : 'en']}
            </div>

            {/* Tagline */}
            <p className={`text-xl md:text-2xl text-gray-100 font-light mb-4 transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              {translations.tagline[isTamil ? 'ta' : 'en']}
            </p>

            {/* Description */}
            <p className={`text-base md:text-lg text-gray-200 mb-8 leading-relaxed transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              {translations.description[isTamil ? 'ta' : 'en']}
            </p>

            {/* CTA Buttons */}
            <div className={`flex flex-wrap gap-4 justify-center transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              <button 
                onClick={() => navigate('/what-we-do')}
                className="group px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2"
              >
                <span>{translations.learnMore[isTamil ? 'ta' : 'en']}</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button 
                onClick={() => navigate('/office-bearers')}
                className="px-8 py-4 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white font-semibold rounded-lg border-2 border-white/30 hover:border-white/50 transition-all duration-300"
              >
                {translations.officeBearers[isTamil ? 'ta' : 'en']}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className="group"
            aria-label={`Go to slide ${index + 1}`}
          >
            <div className={`h-1 rounded-full transition-all duration-300 ${
              currentSlide === index 
                ? 'w-12 bg-white' 
                : 'w-8 bg-white/40 group-hover:bg-white/60'
            }`} />
          </button>
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-8 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default ModernHeroSection;